import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Repeat } from "lucide-react";

const STATUS_COLORS = {
  Pending: "bg-amber-100 text-amber-700 border-amber-200",
  Approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Rejected: "bg-rose-100 text-rose-700 border-rose-200",
};

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const emptyForm = {
  date: "",
  requested_status: "Present",
  check_in: "",
  check_out: "",
  reason: "",
};

export default function MyRegularization() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/attendance/regularizations");
      setItems(r.data || []);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      await api.post("/attendance/regularizations", {
        date: form.date,
        requested_status: form.requested_status,
        reason: form.reason,
        check_in: form.check_in || null,
        check_out: form.check_out || null,
      });
      setOpen(false);
      setForm(emptyForm);
      await load();
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="Attendance Regularization" breadcrumbs={["Home", "Self Service", "Regularization"]}>
      {err && (
        <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
          {err}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500">
          Request a correction for a missed punch or wrong attendance status. HR will review and update your record.
        </p>
        <button
          type="button"
          onClick={() => { setOpen(true); setErr(""); }}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded bg-[#226DB4] hover:bg-[#1B5A94] text-white"
          data-testid="reg-new-btn"
        >
          <Plus className="h-4 w-4" /> New request
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded shadow-sm p-5 mb-4 space-y-3" data-testid="reg-form">
          <h3 className="text-sm font-semibold text-slate-800">Submit regularization</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-xs font-semibold text-slate-500 uppercase">Date</span>
              <input type="date" required className={inputCls} value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold text-slate-500 uppercase">Requested status</span>
              <select className={inputCls} value={form.requested_status}
                onChange={(e) => setForm({ ...form, requested_status: e.target.value })}>
                {["Present", "WFH", "Half-day", "Leave", "Absent"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold text-slate-500 uppercase">Clock in (optional)</span>
              <input type="time" className={inputCls} value={form.check_in}
                onChange={(e) => setForm({ ...form, check_in: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold text-slate-500 uppercase">Clock out (optional)</span>
              <input type="time" className={inputCls} value={form.check_out}
                onChange={(e) => setForm({ ...form, check_out: e.target.value })} />
            </label>
          </div>
          <label className="block text-sm">
            <span className="text-xs font-semibold text-slate-500 uppercase">Reason</span>
            <textarea required rows={3} className={inputCls} value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Forgot to clock in — was in office from 9:30" />
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium rounded bg-[#089949] hover:bg-[#067A3A] text-white disabled:opacity-60">
              {saving ? "Submitting…" : "Submit"}
            </button>
            <button type="button" onClick={() => setOpen(false)}
              className="px-4 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded shadow-sm overflow-x-auto">
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2 text-sm text-slate-600">
          <Repeat className="h-4 w-4 text-[#226DB4]" />
          Your requests
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Date</th>
              <th className="text-left px-4 py-2.5 font-semibold">Current</th>
              <th className="text-left px-4 py-2.5 font-semibold">Requested</th>
              <th className="text-left px-4 py-2.5 font-semibold">In / Out</th>
              <th className="text-left px-4 py-2.5 font-semibold">Reason</th>
              <th className="text-left px-4 py-2.5 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No regularization requests yet</td></tr>
            ) : items.map((r) => (
              <tr key={r.id}>
                <td className="px-4 py-2.5 font-medium text-slate-800">{formatDisplayDate(r.date)}</td>
                <td className="px-4 py-2.5 text-slate-600">{r.current_status || "—"}</td>
                <td className="px-4 py-2.5 text-slate-800">{r.requested_status}</td>
                <td className="px-4 py-2.5 text-slate-600 tabular-nums">
                  {(r.check_in || "—") + " / " + (r.check_out || "—")}
                </td>
                <td className="px-4 py-2.5 text-slate-600 max-w-xs truncate" title={r.reason}>{r.reason}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs border ${STATUS_COLORS[r.status] || ""}`}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
